import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import { useConfirm } from "../context/ConfirmContext";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import { ENABLE_JO_REGISTRY } from "../utils/featureFlags";

const PAGE_SIZE = 20;

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-PH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function JoRegistryPage() {
  const { canAccessJoRegistry, canDeleteJO } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { confirm } = useConfirm();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  // Redirect if feature is disabled or user lacks permission
  useEffect(() => {
    if (!ENABLE_JO_REGISTRY || !canAccessJoRegistry) navigate("/dashboard", { replace: true });
  }, [canAccessJoRegistry, navigate]);

  const fetchRegistry = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/job-orders/registry");
      setItems(data);
      setSelectedIds(new Set());
      setCurrentPage(1);
    } catch {
      setError("Failed to load job order registry.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRegistry();
  }, [fetchRegistry]);

  // Selection (will be updated after search/filter)  
  
  const toggleSelectPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isPageAllSelected) {
        pageItems.forEach((item) => next.delete(item.id));
      } else {
        pageItems.forEach((item) => next.add(item.id));
      }
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDelete = async (item) => {
    const confirmed = await confirm({
      title: "Delete Job Order",
      message: `Permanently delete job order "${item.jo_number}"? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    setDeleting(true);
    setActionError("");
    try {
      await api.delete(`/job-orders/${item.id}`);
      await fetchRegistry();
    } catch (err) {
      setActionError(err.response?.data?.detail || "Failed to delete job order.");
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    const confirmed = await confirm({
      title: "Delete Job Orders",
      message: `Permanently delete ${count} job order${count !== 1 ? "s" : ""}? This cannot be undone.`,
      confirmLabel: "Delete All",
      variant: "danger",
    });
    if (!confirmed) return;

    setDeleting(true);
    setActionError("");
    try {
      await Promise.all([...selectedIds].map((id) => api.delete(`/job-orders/${id}`)));
      await fetchRegistry();
    } catch (err) {
      setActionError(err.response?.data?.detail || "Failed to delete some job orders.");
      await fetchRegistry();
    } finally {
      setDeleting(false);
    }
  };

  const filteredItems = items.filter((item) =>
    item.jo_number?.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pageItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const isPageAllSelected =
    pageItems.length > 0 && pageItems.every((item) => selectedIds.has(item.id));

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {isMobile ? <MobileNavbar /> : <Navbar />}
      <main className="flex-1 p-4 lg:p-6 max-w-[1600px] w-full mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-800">JO Registry</h1>
            <p className="text-xs text-gray-500 mt-1">
              Administrative tool — permanently delete job order records.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search JO Number..."
              className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {selectedIds.size > 0 && (
              <button
                onClick={handleBulkDelete}
                disabled={deleting}
                className="text-sm bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {deleting ? "Deleting…" : `Delete Selected (${selectedIds.size})`}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3 mb-4">
            {error}
          </div>
        )}
        {actionError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3 mb-4">
            {actionError}
          </div>
        )}

        {loading ? (
          <div className="text-center text-gray-400 py-16">Loading…</div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-center w-10">
                      <input
                        type="checkbox"
                        checked={isPageAllSelected}
                        onChange={toggleSelectPage}
                        title="Select all on this page"
                        className="cursor-pointer w-4 h-4"
                      />
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Date Created</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">JO Number</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pageItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                        No job orders found.
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((item) => (
                      <tr
                        key={item.id}
                        className={selectedIds.has(item.id) ? "bg-red-50" : ""}
                      >
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(item.id)}
                            onChange={() => toggleRow(item.id)}
                            className="cursor-pointer w-4 h-4"
                          />
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {formatDateTime(item.created_at)}
                        </td>
                        <td className="px-4 py-3 text-gray-800 font-medium">{item.jo_number}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleDelete(item)}
                            disabled={deleting}
                            className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded disabled:opacity-50"
                          >
                            {deleting ? "Deleting…" : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-gray-500">
                {items.length === 0
                  ? "No records"
                  : `Showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(
                      currentPage * PAGE_SIZE,
                      items.length
                    )} of ${items.length} records`}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-40"
                >
                  ← Prev
                </button>
                <span className="px-3 py-1 text-xs text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
